import React, { useMemo, useRef } from 'react';
import { Box, Button, Text, VStack } from '@chakra-ui/react';
import { useVirtualizer } from '@tanstack/react-virtual';
import type { CategoryItem } from '../types';
import { streamGhostOnDarkHover } from '../darkThemeButtonProps';
import { SearchBar } from './SearchBar';

interface Props {
  categories: CategoryItem[];
  selectedId: string;
  onSelect: (id: string) => void;
  categorySearch?: string;
  onCategorySearchChange?: (q: string) => void;
}

const ROW_H = 34;
/** Match virtual row minus gap under each row (`pb="2px"` on the absolute wrapper). */
const BUTTON_H = ROW_H - 2;
const VIRTUAL_THRESHOLD = 24;

/** Left column category list — same layout as Live TV on large screens. */
export function BrowseCategoryColumn({
  categories,
  selectedId,
  onSelect,
  categorySearch = '',
  onCategorySearchChange,
}: Props) {
  const scrollRef = useRef<HTMLDivElement>(null);

  const rows = useMemo(() => {
    return [{ type: 'all' as const }, ...categories.map((c) => ({ type: 'cat' as const, c }))];
  }, [categories]);

  const useVirtual = rows.length >= VIRTUAL_THRESHOLD;

  const virtualizer = useVirtualizer({
    count: useVirtual ? rows.length : 0,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_H,
    overscan: 12,
  });

  const vItems = useVirtual ? virtualizer.getVirtualItems() : [];

  const renderButton = (key: string, isAll: boolean, cat?: CategoryItem) => {
    const sel = isAll ? selectedId === 'all' : cat && selectedId === cat.id;
    return (
      <Button
        key={key}
        size="xs"
        variant={sel ? 'solid' : 'ghost'}
        colorScheme="teal"
        display="flex"
        flexDirection="row"
        alignItems="center"
        justifyContent="flex-start"
        textAlign="left"
        w="100%"
        h={`${BUTTON_H}px`}
        minH={`${BUTTON_H}px`}
        maxH={`${BUTTON_H}px`}
        px={2}
        py={0}
        lineHeight="1.2"
        color={sel ? undefined : 'whiteAlpha.900'}
        {...(!sel ? streamGhostOnDarkHover : {})}
        onClick={() => onSelect(isAll ? 'all' : cat!.id)}
      >
        {isAll ? (
          <Text as="span" noOfLines={1} textAlign="left" w="100%" lineHeight="1.2">
            All
          </Text>
        ) : (
          <Text noOfLines={2} textAlign="left" w="100%" lineHeight="1.2">
            {cat!.name}
          </Text>
        )}
      </Button>
    );
  };

  return (
    <Box
      w="100%"
      h="100%"
      minH={0}
      flexShrink={0}
      borderRightWidth="1px"
      borderColor="whiteAlpha.100"
      display="flex"
      flexDirection="column"
      alignSelf="stretch"
      maxH="100%"
    >
      {onCategorySearchChange && (
        <Box mb={2} flexShrink={0}>
          <SearchBar
            compact
            value={categorySearch}
            onChange={onCategorySearchChange}
            placeholder="Categories (2+ chars)"
          />
        </Box>
      )}
      <Text fontSize="10px" fontWeight="700" color="whiteAlpha.400" textTransform="uppercase" mb={2} px={2} flexShrink={0} textAlign="left">
        Categories
      </Text>
      <Box ref={scrollRef} flex={1} minH={0} overflowY="auto" pr={2}>
        <Box display="flex" flexDirection="column" justifyContent="flex-start">
          {useVirtual ? (
            <Box position="relative" w="100%" h={`${virtualizer.getTotalSize()}px`} flexShrink={0}>
              {vItems.map((vi) => {
                const row = rows[vi.index];
                const key = row.type === 'all' ? 'all' : row.c.id;
                return (
                  <Box
                    key={key}
                    position="absolute"
                    top={0}
                    left={0}
                    w="100%"
                    transform={`translateY(${vi.start}px)`}
                    pb="2px"
                  >
                    {row.type === 'all'
                      ? renderButton('all', true)
                      : renderButton(row.c.id, false, row.c)}
                  </Box>
                );
              })}
            </Box>
          ) : (
            <VStack align="stretch" spacing={1} w="100%" flexShrink={0}>
              {renderButton('all', true)}
              {categories.map((c) => renderButton(c.id, false, c))}
            </VStack>
          )}
        </Box>
      </Box>
    </Box>
  );
}
